/*************************************************************************
* ADOBE SYSTEMS INCORPORATED
*  Copyright 2016 Adobe Systems Incorporated
*  All Rights Reserved.
*
* NOTICE:  Adobe permits you to use, modify, and distribute this file in
* accordance with the terms of the Adobe license agreement accompanying it.
* If you have received this file from a source other than Adobe, then your
* use, modification, or distribution of it requires the prior written
* permission of Adobe.
**************************************************************************/
/*jslint node:true, browser:true, nomen:true, maxlen:120 */
/*eslint no-ex-assign: 0 */
/*eslint-env browser */
/*global */
'use strict';

/**
 * AdobeDC API support with ES6 Promise and XMLHttpRequest for AdobeDC.http.
 */
var AdobeDC = require('../src/adobe-dc.js');

module.exports = {
    // This expects the standard ES6 Promise global to exist.
    // For older browsers, use a Promise polyfill such as the lean "es6-promise-polyfill" or larger "es6-promises".
    Promise : Promise,
    http : {
        open : function (method, url, async) {  // async default true
            var xhr = new XMLHttpRequest();
            xhr.open(method, url, async !== false);
            xhr.setRequestHeader("X-Requested-With", "XMLHttpRequest");
            return xhr;
        },
        parseHeaders : function (text) {
            var headers = {}, lines = String(text).split("\n");
            lines.forEach(function (line) {
                var colon = line.indexOf(": ");
                if (colon !== -1) {
                    headers[line.substring(0, colon)] = line.substring(colon + 2).replace(/\r$/, "");
                }
            });
            return headers;
        },
        getHeader : function (map, name) {
            if (!map || !name) { return; }
            var result = map[name];
            return result !== undefined ? result : map[String(name).toLowerCase()];
        },
        encodeUriMap : function (obj) {
            return Object.keys(obj || {})
                .map(function(key) { return encodeURIComponent(key) + "=" + encodeURIComponent(obj[key]) })
                .join("&")
        },
        call : function (method, url, options, content) {
            var self = this, xhr, timeout, promise;
            options = options || {};
            timeout = options.timeout;
            if (!(timeout >= 0)) { timeout = self.timeout; }
            promise = new Promise(function executor(fulfill, reject) {
                try {
                    xhr = self.open(method, url);
                    if (timeout >= 0) { xhr.timeout = timeout; }
                    if (options.responseType) { xhr.responseType = options.responseType; }
                    Object.keys(options.headers || {}).forEach(function(prop) {
                        xhr.setRequestHeader(prop, options.headers[prop]);
                    });
                    if (options.withCredentials) { xhr.withCredentials = options.withCredentials; }
                    xhr.onreadystatechange = function () {
                        if (!options.response_content_open) {
                            // Return if waiting for the entire response and it's not done.
                            if (xhr.readyState !== 4) {
                                return;
                            }
                        } else {
                            // Return if waiting for response headers and they're not available.
                            if (!xhr.readyState || xhr.readyState < 2) {
                                return;
                            }
                        }
                        var result;
                        try {
                            result = {
                                status: xhr.status,
                                statusText: xhr.statusText || xhr.status,
                                headers: self.parseHeaders(xhr.getAllResponseHeaders()),
                                content: undefined
                            };
                            // Process the response content unless told otherwise.
                            if (!options.response_content_open) {
                                if (xhr.responseType && xhr.responseType !== "text") {
                                    result.content = xhr.response;
                                } else {
                                    result.content = xhr.responseText;
                                }
                            }
                            if (xhr.status > 0 && xhr.status < 400) {
                                fulfill(result);
                            } else {
                                if (xhr.status === 0 && !result.statusText) {
                                    result.statusText = "Timeout or network error";
                                }
                                throw new AdobeDC.AdobeError(result.statusText, result.status, result);
                            }
                        } catch (err) {
                            if (err && !(err instanceof Error)) {
                                err = new AdobeDC.AdobeError(err.stack || err, result.status, result);
                            }
                            reject(err);
                        }
                    };
                    if (!options.request_content_open) { xhr.send(content); }
                } catch (err) {
                    err = new AdobeDC.AdobeError(err.stack || err, 0, {
                        method: method,
                        url: url,
                        options: options
                    });
                    reject(err);
                }
            });
            if (options.request_content_open || options.response_content_open) {
                promise.request = xhr;
            }
            return promise;
        }
    }
};

["OPTIONS", "HEAD", "GET", "POST", "PUT", "PATCH", "DELETE"].forEach(function (key) {
    module.exports.http[key.toLowerCase()] = function (url, options, content) {
        return this.call(key, url, options, content);
    };
});
